// A line's certs: every 8130-3 / EASA Form 1 / CoC / trace doc tied to it.
// Any number — one per unit, one per vendor, a dual release and its CoC.
// They are RFQ files like any other (rfq-files-panel.tsx), shown here by line.

import { useRef } from 'react';
import { Paperclip, ShieldCheck } from 'lucide-react';
import {
  CERT_KINDS, RFQ_DOCUMENT_ACCEPT, RFQ_DOCUMENT_KIND_LABELS, certKindForRequested,
} from '@shared/rfq-documents';
import { RfqFileLink, UploadQueueList, useRfqDocuments, useRfqUploadQueue } from './rfq-files-shared';

interface Props {
  rfqPublicId: string;
  lineId: string;
  certRequested: string | null;
}

export function LineCertificates({ rfqPublicId, lineId, certRequested }: Props) {
  const { data } = useRfqDocuments(rfqPublicId);
  const { queue, add, dismiss } = useRfqUploadQueue(rfqPublicId);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const certs = (data?.documents ?? []).filter((d) =>
    d.lineItemId === lineId && CERT_KINDS.has(d.kind) && !d.removedAt && d.legacy?.source !== 'replaced_line_cert');

  function onPicked(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    const kind = certKindForRequested(certRequested);
    if (files.length) add(files.map((file) => ({ file, kind, lineItemId: lineId })));
  }

  return (
    <div className="mt-2 text-xs space-y-1">
      <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
        {certs.map((d, i) => (
          <span key={d.id ?? `${d.legacy?.objectKey}-${i}`} className="inline-flex items-center gap-1 min-w-0">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" aria-hidden />
            <RfqFileLink rfqPublicId={rfqPublicId} doc={d} className="text-primary hover:underline font-medium truncate max-w-[14rem]" />
            <span className="text-muted-foreground">{RFQ_DOCUMENT_KIND_LABELS[d.kind]}</span>
          </span>
        ))}
        <button type="button" onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-1 px-2 py-2 border border-border rounded hover:bg-muted text-muted-foreground sm:px-1.5 sm:py-0.5">
          <Paperclip className="w-3 h-3" aria-hidden />
          {certs.length ? 'Add more certs' : '+ Attach certs'}
        </button>
        {!certs.length && (
          <span className="text-[11px] text-muted-foreground italic">8130-3 / EASA Form 1 / CoC / trace. Select several at once.</span>
        )}
      </div>
      <UploadQueueList queue={queue} onDismiss={dismiss} />
      <input ref={inputRef} type="file" multiple accept={RFQ_DOCUMENT_ACCEPT} onChange={onPicked} className="hidden" />
    </div>
  );
}
